import { useState } from 'react';

import { Box, Flex, Grid } from '@chakra-ui/react';
import { L3_PATHS } from 'Deer/constants/routes';
import { useHistory } from 'react-router-dom';

import { Check, Publish } from 'assets/assetsV1/iconComponents';
import { ArrowDropRight } from 'assets/assetsV1/iconComponents/ArrowDropRight';
import { Bolt } from 'assets/assetsV1/iconComponents/Bolt';
import { Propose } from 'assets/assetsV1/iconComponents/Propose';
import { Recurring } from 'assets/assetsV1/iconComponents/Recurring';
import AvatarIcon from 'assets/assetsV1/illustrations/deerIllustrations/compensationPresetTypes/avatar.svg';
import CustomIcon from 'assets/assetsV1/illustrations/deerIllustrations/compensationPresetTypes/custom.svg';
import GrowthIcon from 'assets/assetsV1/illustrations/deerIllustrations/compensationPresetTypes/growth.svg';

import { Button } from 'uiToolkitV1/Button';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { RadioCard } from 'uiToolkitV1/RadioCard/RadioCard';
import { TextLabel } from 'uiToolkitV1/Typography';

import { CompensationCreationHeader } from './components/CompensationCreationHeader';

interface CompensationPreset {
  id: string;
  title: string;
  description: string;
  icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
  cycleType: 'ONE_TIME' | 'CONTINUOUS';
  trigger: 'MANUAL' | 'WORK_ANNIVERSARY' | 'START_DATE' | 'FIXED';
  workflow: string[];
  timing: string;
}

const compensationPresets: CompensationPreset[] = [
  {
    id: 'WORK_ANNIVERSARY',
    title: 'Work-anniversary compensation review',
    description: "Automatically launch compensation reviews around each employee's work anniversary",
    icon: GrowthIcon,
    cycleType: 'CONTINUOUS',
    trigger: 'WORK_ANNIVERSARY',
    workflow: ['Propose', 'Approve', 'Publish'],
    timing: '14 days before anniversary',
  },
  {
    id: 'ANNUAL_CYCLE',
    title: 'Annual compensation cycle',
    description: 'Tie annual compensation decisions to performance reviews for consistent, fair rewards.',
    icon: AvatarIcon,
    cycleType: 'ONE_TIME',
    trigger: 'MANUAL',
    workflow: ['Propose', 'Approve', 'Publish'],
    timing: 'After review is published',
  },
  {
    id: 'HALF_YEARLY',
    title: 'Half yearly compensation cycle',
    description: 'Run compensation reviews every six months to align with internal growth and market changes',
    icon: GrowthIcon,
    cycleType: 'CONTINUOUS',
    trigger: 'WORK_ANNIVERSARY',
    workflow: ['Propose', 'Approve', 'Publish'],
    timing: 'On scheduled date',
  },
  {
    id: 'AD_HOC',
    title: 'Ad-hoc compensation cycle',
    description: 'Create one-time cycles for promotion, retention, or special case outside regular plans',
    icon: AvatarIcon,
    cycleType: 'ONE_TIME',
    trigger: 'MANUAL',
    workflow: ['Propose', 'Publish'],
    timing: 'When manually started',
  },
  {
    id: 'CUSTOM',
    title: 'Custom cycle',
    description: 'A flexible setup allowing you to customize all elements of the compensation as needed.',
    icon: CustomIcon,
    cycleType: 'ONE_TIME',
    trigger: 'MANUAL',
    workflow: [],
    timing: 'None',
  },
];

const PresetTags = ({ preset }: { preset: CompensationPreset }) => {
  const tags = [];
  if (preset.cycleType === 'ONE_TIME') {
    tags.push(
      <Flex bg="#F2F2F2" p="4px" borderRadius="4px" gap="2px">
        <Recurring color="text.g2" />
        <TextLabel>One-time</TextLabel>
      </Flex>
    );
  } else if (preset.cycleType === 'CONTINUOUS') {
    tags.push(
      <Flex bg="#F2F2F2" p="4px" borderRadius="4px" gap="2px">
        <Recurring color="text.g2" />
        <TextLabel>Continuous</TextLabel>
      </Flex>
    );
  }

  if (preset.trigger === 'MANUAL') {
    tags.push(
      <Flex bg="#F2F2F2" p="4px" borderRadius="4px" gap="2px">
        <Bolt color="text.g2" />
        <TextLabel>Manual</TextLabel>
      </Flex>
    );
  } else if (preset.trigger === 'WORK_ANNIVERSARY') {
    tags.push(
      <Flex bg="#F2F2F2" p="4px" borderRadius="4px" gap="2px">
        <Bolt color="text.g2" />
        <TextLabel>Work anniversary</TextLabel>
      </Flex>
    );
  }

  if (preset.workflow.length > 0) {
    tags.push(
      <Flex bg="#F2F2F2" p="4px" borderRadius="4px" gap="2px">
        {preset.workflow.map((workflow, index) => (
          <Flex key={workflow} gap="2px" alignItems="center">
            {workflow === 'Propose' && <Propose height="12px" width="12px" color="text.g2" />}
            {workflow === 'Approve' && <Check color="text.g2" />}
            {workflow === 'Publish' && <Publish color="text.g2" />}
            <TextLabel>{workflow}</TextLabel>
            {index < preset.workflow.length - 1 && <ArrowDropRight color="text.g2" />}
          </Flex>
        ))}
      </Flex>
    );
  }

  if (tags.length === 0) {
    tags.push(
      <Flex bg="#F2F2F2" p="4px" borderRadius="4px" gap="2px">
        <TextLabel>None</TextLabel>
      </Flex>
    );
  }

  return (
    <Flex wrap="wrap" gap="4px">
      {tags}
    </Flex>
  );
};

export const CompensationPresets = () => {
  const history = useHistory();
  const [selectedPreset, setSelectedPreset] = useState<string>('CUSTOM');

  const handlePresetSelect = (presetId: string) => {
    setSelectedPreset(presetId);
  };

  const handleSelectTemplate = () => {
    history.push(
      L3_PATHS.company.compensation.compensationCycleCreation.replace(':compensationCycleId', selectedPreset)
    );
  };

  return (
    <Flex flexDirection="column" w="100%" h="100%" maxH="calc(100vh - 20px)" gap="12px">
      <CompensationCreationHeader title="Create a new compensation cycle" />
      <PaperWithShadow display="flex" flexDirection="column" flex="1" p="0px" overflow="hidden">
        <Box flex="1" p="12px" overflow="auto">
          <Box bg="#F5F7F9" p="8px" borderRadius="8px" minH="100%">
            <Grid
              gridTemplateColumns={{
                base: '1fr',
                md: '1fr 1fr',
              }}
              gridGap={2}
            >
              {compensationPresets.map((preset) => (
                <RadioCard
                  key={preset.id}
                  title={preset.title}
                  subText={preset.description}
                  imgSrc={preset.icon}
                  checked={selectedPreset === preset.id}
                  onClick={() => handlePresetSelect(preset.id)}
                  additionalContent={<PresetTags preset={preset} />}
                  minH="140px"
                  h="auto"
                />
              ))}
            </Grid>
          </Box>
        </Box>
        <Flex
          bg="surface.F5F7F9"
          alignItems="center"
          justifyContent="flex-end"
          h="72px"
          px="16px"
          boxShadow="0px -1px 2px 0px rgba(0, 0, 0, 0.08)"
          flexShrink={0}
        >
          <Button variant="primary" onClick={handleSelectTemplate}>
            Next
          </Button>
        </Flex>
      </PaperWithShadow>
    </Flex>
  );
};
